import { Link } from 'react-router-dom';
import { useMediaQuery, MOBILE_BREAKPOINT } from '../../../hooks/useMediaQuery';
import { useSiteDetails } from '../hooks/useSiteDetails';
import Card from '../../../components/ui/Card';
import Tabs from '../../../components/ui/Tabs';
import SiteDetailsHeader from './SiteDetailsHeader';
import SiteDetailsInfoCard from './SiteDetailsInfoCard';
import SiteDetailsAssetsOverview from './SiteDetailsAssetsOverview';
import SiteDetailsRepairsPanel from './SiteDetailsRepairsPanel';
import SiteDetailsHistoryPanel from './SiteDetailsHistoryPanel';

export default function SiteDetails() {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const {
    id,
    activeTab,
    setActiveTab,
    sitesLoading,
    sitesError,
    site,
    siteName,
    repairCount,
    repairs,
    repairsLoading,
    siteSessions,
    sessionsLoading,
    canDeleteSessions,
    deleteSession,
    isDeletingSession,
    inProgressSessionsThisSite,
    assetsOverview,
    elementTypeLabels,
    handleStartNewTest,
    handleRepairSession,
    addressLine,
    addressMapsUrl,
    nextDue,
    statusLabel,
    statusClass,
    siteDataWarning,
  } = useSiteDetails();

  if (sitesLoading) {
    return <div className="page-loading">Loading…</div>;
  }

  if (sitesError || !site) {
    return (
      <div className="page">
        <div className="page-error">
          {sitesError ? <p>Failed to load site.</p> : <p>Site not found.</p>}
        </div>
        <p>
          <Link to="/sites" className="link">Back to Sites</Link>
        </p>
      </div>
    );
  }

  return (
    <div className="page">
      <SiteDetailsHeader
        isMobile={isMobile}
        siteId={id}
        siteName={siteName}
        inProgressSessionsThisSite={inProgressSessionsThisSite}
        onStartNewTest={handleStartNewTest}
      />

      {siteDataWarning ? (
        <p className="text-muted" style={{ marginBottom: '1rem' }}>
          Some site details could not be loaded and may be incomplete.
        </p>
      ) : null}

      <SiteDetailsInfoCard
        siteCode={site.site_code?.trim() || '—'}
        addressLine={addressLine}
        addressMapsUrl={addressMapsUrl}
        nextDue={nextDue}
        statusLabel={statusLabel}
        statusClass={statusClass}
      />

      <SiteDetailsAssetsOverview
        assetsOverview={assetsOverview}
        elementTypeLabels={elementTypeLabels}
      />

      {/* Tabs */}
      <Tabs
        className="site-details-tabs"
        style={{ marginBottom: '1rem' }}
        activeKey={activeTab}
        onChange={setActiveTab}
        tabs={[
          { key: 'overview', label: 'Overview' },
          { key: 'repairs', label: `Outstanding Repairs ${repairCount > 0 ? repairCount : ''}`.trim() },
          { key: 'history', label: 'Test History' },
          { key: 'reports', label: 'Reports' },
        ]}
      />

      {activeTab === 'overview' && (
        <Card>
          <p className="text-muted">Site summary: {siteName}. {assetsOverview.total} assets. {repairCount > 0 ? `${repairCount} outstanding repair(s).` : 'No outstanding repairs.'}</p>
        </Card>
      )}

      {activeTab === 'repairs' && (
        <SiteDetailsRepairsPanel
          repairCount={repairCount}
          repairs={repairs}
          repairsLoading={repairsLoading}
          onRepairSession={handleRepairSession}
        />
      )}

      {activeTab === 'history' && (
        <SiteDetailsHistoryPanel
          siteSessions={siteSessions}
          sessionsLoading={sessionsLoading}
          canDeleteSessions={canDeleteSessions}
          onDeleteSession={deleteSession}
          isDeletingSession={isDeletingSession}
        />
      )}

      {activeTab === 'reports' && (
        <Card>
          <p className="text-muted" style={{ marginBottom: '1rem' }}>Session and defect reports are available from the Reports page.</p>
          <Link to="/reports" className="btn btn-primary">Open Reports</Link>
        </Card>
      )}
    </div>
  );
}
